import React, { useCallback, useEffect, useState } from 'react';
import { Rocket, RefreshCw, Database, CheckCircle2, AlertTriangle, ArrowUpCircle, Bot, Zap } from 'lucide-react';
import trainingService, { FineTuneJob, AutomationStatus } from '../../services/trainingService';
import { PageHeader, StatusPill, Spinner, EmptyState, Button, IconButton } from '../../components/ui';
import '../../styles/theme.css';

const BASE_MODELS = ['llama-3.1-8b', 'qwen2.5-7b', 'mistral-7b'];

const Training: React.FC = () => {
  const [examples, setExamples] = useState<number | null>(null);
  const [jobs, setJobs] = useState<FineTuneJob[]>([]);
  const [name, setName] = useState('');
  const [baseModel, setBaseModel] = useState(BASE_MODELS[0]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [msg, setMsg] = useState('');

  const [auto, setAuto] = useState<AutomationStatus | null>(null);

  const refresh = useCallback(async () => {
    const [d, j, a] = await Promise.all([
      trainingService.datasetPreview(), trainingService.jobs(),
      trainingService.getAutomation().catch(() => null),
    ]);
    setExamples(d.examples); setJobs(j.jobs); setAuto(a);
  }, []);

  const patchAuto = async (cfg: Parameters<typeof trainingService.setAutomation>[0]) => {
    const next = await trainingService.setAutomation(cfg);
    setAuto(next);
  };

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  const start = async () => {
    if (!name.trim()) { setMsg('Give the fine-tune a name.'); return; }
    setStarting(true); setMsg('');
    try {
      const { job } = await trainingService.start(name.trim(), baseModel);
      setMsg(job.status === 'failed' ? `Submit failed: ${job.error}`
        : `Submitted "${job.name}" (${job.dataset_size} examples).`);
      setName('');
      refresh();
    } catch (e: any) { setMsg(e?.response?.data?.detail || 'Start failed'); }
    finally { setStarting(false); }
  };

  const refreshJob = async (id: string) => { await trainingService.job(id); refresh(); };
  const promote = async (id: string) => {
    setMsg('Evaluating candidate vs incumbent on the golden holdout…');
    try {
      const { job } = await trainingService.promote(id);
      const es: any = job.eval_summary || {};
      setMsg(es.promoted
        ? `Promoted ✓ candidate F1 ${es.candidate?.f1} ≥ incumbent ${es.incumbent?.f1} — the OSS stream now uses this model for public docs.`
        : `Kept incumbent — candidate F1 ${es.candidate?.f1} < incumbent ${es.incumbent?.f1} on ${es.holdout} docs.`);
      refresh();
    } catch (e: any) { setMsg(e?.response?.data?.detail || 'Promote failed'); }
  };

  if (loading) return <div><PageHeader title="Training" /><div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div></div>;

  const canTrain = (examples ?? 0) > 0;

  return (
    <div>
      <PageHeader title="Training"
        subtitle="Fine-tune an open-source model on your approved/corrected extractions, then let the eval suite decide promotion." />

      {msg && <div className="mx-card mx-card-pad" style={{ marginBottom: 16, fontSize: 13 }}>{msg}</div>}

      <div className="mx-grid" style={{ gridTemplateColumns: '260px 1fr', marginBottom: 24, alignItems: 'stretch' }}>
        <div className="mx-card mx-card-pad" style={{ textAlign: 'center' }}>
          <Database size={20} color="var(--mx-accent)" />
          <div className="mx-num" style={{ fontSize: 30, fontWeight: 700, marginTop: 6 }}>{examples ?? 0}</div>
          <div className="mx-muted" style={{ fontSize: 13 }}>training examples (golden-labeled docs)</div>
        </div>
        <div className="mx-card mx-card-pad">
          <h3 className="mx-card-title" style={{ marginBottom: 12 }}>Start a fine-tune</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label className="mx-subtle" style={{ fontSize: 12, fontWeight: 600 }}>Name</label>
              <input className="mx-input" placeholder="docshawk-extractor-v1" value={name}
                onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="mx-subtle" style={{ fontSize: 12, fontWeight: 600 }}>Base model</label>
              <select className="mx-select" value={baseModel} onChange={(e) => setBaseModel(e.target.value)}>
                {BASE_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <Button icon={Rocket} variant="primary" onClick={start} disabled={starting || !canTrain}>
              {starting ? 'Submitting…' : 'Start fine-tune'}</Button>
          </div>
          {!canTrain && <div style={{ color: 'var(--mx-warning)', fontSize: 12, marginTop: 10,
            display: 'flex', gap: 6, alignItems: 'center' }}>
            <AlertTriangle size={14} /> Approve/correct some runs first — training needs golden-labeled documents.</div>}
          <div className="mx-subtle" style={{ fontSize: 11, marginTop: 10 }}>
            Runs on the LaptopPool managed API (set LAPTOPOOL_KEY in Settings). The golden dataset is sent to the provider.</div>
        </div>
      </div>

      {/* Automation — self-driving loop */}
      {auto && (
        <div className="mx-card mx-card-pad" style={{ marginBottom: 24 }}>
          <h3 className="mx-card-title" style={{ marginBottom: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Bot size={16} /> Automation</h3>
          <div className="mx-subtle" style={{ fontSize: 12, marginBottom: 16 }}>
            Let the pipeline train and promote on its own. Auto-train spends money, so it's off until you enable it.</div>

          <div className="mx-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Auto-train */}
            <div style={{ border: '1px solid var(--mx-border)', borderRadius: 8, padding: 14 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: 14 }}>
                <input type="checkbox" checked={auto.auto_train_enabled}
                  onChange={(e) => patchAuto({ auto_train_enabled: e.target.checked })} />
                <Rocket size={14} /> Auto-train on new data</label>
              <div className="mx-subtle" style={{ fontSize: 12, margin: '6px 0 10px' }}>
                Submit a fine-tune once this many new golden labels accrue since the last job.</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="mx-subtle" style={{ fontSize: 12 }}>Threshold</span>
                <input className="mx-input" type="number" min={1} style={{ width: 80 }}
                  value={auto.auto_train_threshold}
                  onChange={(e) => setAuto({ ...auto, auto_train_threshold: Number(e.target.value) })}
                  onBlur={(e) => patchAuto({ auto_train_threshold: Number(e.target.value) })} />
                <span className="mx-pill" style={{ background: 'var(--mx-surface-2)' }}>
                  {auto.new_golden_since_last_job} new since last job</span>
              </div>
              <div style={{ marginTop: 8 }}>
                <span className="mx-subtle" style={{ fontSize: 12, marginRight: 6 }}>Base model</span>
                <input className="mx-input mx-mono" style={{ width: 160 }}
                  value={auto.auto_train_base_model}
                  onChange={(e) => setAuto({ ...auto, auto_train_base_model: e.target.value })}
                  onBlur={(e) => patchAuto({ auto_train_base_model: e.target.value })} />
              </div>
            </div>

            {/* Auto-promote + champions */}
            <div style={{ border: '1px solid var(--mx-border)', borderRadius: 8, padding: 14 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: 14 }}>
                <input type="checkbox" checked={auto.auto_promote_enabled}
                  onChange={(e) => patchAuto({ auto_promote_enabled: e.target.checked })} />
                <ArrowUpCircle size={14} /> Auto-promote winners</label>
              <div className="mx-subtle" style={{ fontSize: 12, margin: '6px 0 10px' }}>
                When a fine-tune finishes, evaluate it on the holdout and promote only if it beats the incumbent — then re-benchmark.</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                <Zap size={13} color="var(--mx-accent)" /> Routing champions</div>
              {Object.keys(auto.champions).length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.entries(auto.champions).map(([stream, prov]) => (
                    <span key={stream} className="mx-pill" style={{ background: 'var(--mx-success-weak)', color: 'var(--mx-success)' }}>
                      {stream} → {prov}</span>
                  ))}
                </div>
              ) : <div className="mx-subtle" style={{ fontSize: 12 }}>none yet — run a benchmark to elect one.</div>}
            </div>
          </div>
        </div>
      )}

      <div className="mx-card">
        <div className="mx-card-head">
          <h3 className="mx-card-title">Fine-tune jobs</h3>
          <Button icon={RefreshCw} variant="ghost" size="sm" onClick={refresh}>Refresh</Button>
        </div>
        {!jobs.length ? (
          <div className="mx-card-pad"><EmptyState icon={<Rocket size={26} />} title="No jobs yet"
            hint="Start a fine-tune above." /></div>
        ) : (
          <table className="mx-table">
            <thead><tr><th>Name</th><th>Base</th><th>Status</th><th>Examples</th><th>Result model</th><th></th></tr></thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td style={{ fontWeight: 500 }}>{j.name}</td>
                  <td className="mx-mono">{j.base_model}</td>
                  <td><StatusPill status={j.status === 'promoted' ? 'succeeded' : j.status} />
                    {j.error && <div style={{ color: 'var(--mx-danger)', fontSize: 11 }}>{j.error}</div>}</td>
                  <td>{j.dataset_size}</td>
                  <td className="mx-mono mx-subtle">{j.result_model || '—'}
                    {j.eval_summary?.candidate && (
                      <div style={{ fontSize: 11, marginTop: 2 }}>
                        F1 {j.eval_summary.candidate.f1} vs {j.eval_summary.incumbent?.f1}
                      </div>)}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <IconButton icon={RefreshCw} label="Refresh status" onClick={() => refreshJob(j.id)} />
                    {j.result_model && j.status !== 'promoted' && (
                      <Button icon={ArrowUpCircle} variant="secondary" size="sm" onClick={() => promote(j.id)}>
                        Evaluate &amp; promote</Button>)}
                    {j.status === 'promoted' && <span className="mx-pill" style={{ background: 'var(--mx-success-weak)', color: 'var(--mx-success)' }}><CheckCircle2 size={12} /> promoted</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Training;
